import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams, Link, useNavigate } from 'react-router-dom';

import { AuthenticationStatus } from '@/contracts';
import { registration as actions } from '@/actions';
import { useAppSelector } from '@/state/store';
import { showMessage } from '@/actions/common';
import t from '@/utils/translate';

type Props = {
  redirectUrl: string;
};

const authenticatedRedirectUrl = "/";

export default ({ redirectUrl }: Props) => {
  const { code } = useParams();
  const authenticated = useAppSelector((state) => state.authentication.status == AuthenticationStatus.AUTHENTICATED);
  const nav = useNavigate();

  const [redirectTimeout, setRedirectTimeout] = useState<number | null>(null);

  const redirect = useCallback(() => {
    nav(authenticated ? authenticatedRedirectUrl : redirectUrl, { replace: true });
    nav(0);
  }, [ authenticated ]);

  const onConfirmed = useCallback(async () => {
    if (authenticated) {
      showMessage(t('confirmation.email.message.confirmed'), 'success');
      nav(authenticatedRedirectUrl, { replace: true });
      nav(0);
      return;
    }

    const timeout = window.setTimeout(() => {
      redirect();
    }, 10000);

    actions.resetRegistration();
    setRedirectTimeout(timeout);
  }, [ authenticated ]);

  useEffect(() => {
    if (code && !redirectTimeout) {
      actions.confirmByEmail(code)
        .then(onConfirmed)
        .catch(redirect);
    }
  }, [ redirectTimeout, code ]);

  useEffect(() => {
    return () => {
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    }
  }, [ redirectTimeout ]);

  if (code) {
    if (!redirectTimeout) {
      return <h3>{t('registration.title.emailActivation', true)}</h3>;
    }

    return (<>
      <h3>{t('registration.message.activated', true)}</h3>
      <p>{t('registration.hint.nowYouCan', true)} <Link to="/login">{t('registration.hint.enterApp')}</Link>.</p>
    </>);
  }

  return <Navigate to={authenticated ? authenticatedRedirectUrl : redirectUrl} replace/>;
};
